import unionWith from 'lodash/unionWith';
import omit from 'lodash/omit';

import config from '../../config';
import { storableError } from '../../util/errors';
import { createImageVariantConfig, types as sdkTypes } from '../../util/sdkLoader';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { getUserBagData, getUserSaveLaterData, localBagData } from '../../util/dataExtractor';
import { saveLaterItemReminderEmailToUser } from '../../util/api';
import { isStockInUse } from '../../util/search';
import { LISTING_STATE_PUBLISHED } from '../../util/types';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 12 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 24;
const { UUID } = sdkTypes;

// ================ Action types ================ //

export const FETCH_LISTING_REQUEST = 'app/CartPage/FETCH_LISTING_REQUEST';
export const FETCH_LISTING_SUCCESS = 'app/CartPage/FETCH_LISTING_SUCCESS';
export const FETCH_LISTING_ERROR = 'app/CartPage/FETCH_LISTING_ERROR';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  fetchListingInProgress: false,
  fetchListingError: null,
  currentPageResultIds: [],
  // Bag items that the listings query no longer returns (closed by the seller). They stay in
  // the bag, flagged as unavailable, and are left out of checkout.
  unavailableIds: [],
};

const resultIds = data => data.data.map(l => l.id);

/**
 * Ids in requestedIds that are missing from a listings query response.
 *
 * @param {Array<String>} requestedIds listing uuids
 * @param {Object} responseData `response.data` of `sdk.listings.query`
 * @returns {Array<String>}
 */
export const missingListingIds = (requestedIds, responseData) => {
  const returnedIds = ((responseData && responseData.data) || []).map(l => l.id.uuid);
  return (requestedIds || []).filter(id => id && !returnedIds.includes(id));
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LISTING_REQUEST:
      return {
        ...state,
        fetchListingInProgress: true,
        currentPageResultIds: [],
        unavailableIds: [],
        fetchListingError: null,
      };
    case FETCH_LISTING_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        unavailableIds: payload.unavailableIds || [],
        pagination: payload.data.meta,
        fetchListingInProgress: false,
      };
    case FETCH_LISTING_ERROR:
      // eslint-disable-next-line no-console
      return { ...state, fetchListingInProgress: false, fetchListingError: payload };

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const fetchListingRequest = () => ({
  type: FETCH_LISTING_REQUEST,
});

export const fetchListingSuccess = (response, unavailableIds = []) => ({
  type: FETCH_LISTING_SUCCESS,
  payload: { data: response.data, unavailableIds },
});

export const fetchListingError = e => ({
  type: FETCH_LISTING_ERROR,
  error: true,
  payload: e,
});

export const saveLaterNotification = emailData => async (dispatch, getState, sdk) => {
  try {
    const result = await saveLaterItemReminderEmailToUser(emailData);
    if (result) {
      return result;
    }
  } catch (error) {}
};

export const fetchListings = params => async (dispatch, getState, sdk) => {
  const { bookmarks, saveLaterItems } = params;
  dispatch(fetchListingRequest());

  try {
    const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = config.listing;
    const aspectRatio = aspectHeight / aspectWidth;

    const filteredIds = bookmarks.map(listing => {
      if (typeof listing === 'object') return listing.id;
      else return listing;
    });
    const minStockMaybe = isStockInUse(config) ? { minStock: 1 } : {};

    const modifiedIds = [...filteredIds, ...(saveLaterItems ? saveLaterItems.map(e => e.id) : [])];
    const queryParams = {
      // ...minStockMaybe,
      ids: modifiedIds,
      perPage: RESULT_PAGE_SIZE,
      pub_isDefaultForMessage: false,
      include: ['author', 'images', 'currentStock'],
      'fields.listing': ['title', 'geolocation', 'price', 'description', 'publicData'],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
      'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    };
    const response = await sdk.listings.query(queryParams);
    dispatch(addMarketplaceEntities(response));

    // The query only returns published listings. Fetch bag items it left out one by one so a
    // closed listing can still be shown in the bag, flagged as unavailable. A deleted listing
    // cannot be fetched and drops out as before. A published listing left out only by the page
    // size is not flagged.
    const missingBagIds = missingListingIds(filteredIds, response.data);
    const showParams = omit(queryParams, ['ids', 'perPage', 'pub_isDefaultForMessage']);
    const closedResponses = await Promise.all(
      missingBagIds.map(id =>
        sdk.listings
          .show({
            ...showParams,
            id: new UUID(id),
            'fields.listing': [...queryParams['fields.listing'], 'state'],
          })
          .catch(() => null)
      )
    );
    const unavailableIds = closedResponses
      .filter(r => r && r.data.data.attributes.state !== LISTING_STATE_PUBLISHED)
      .map(closedResponse => {
        dispatch(addMarketplaceEntities(closedResponse));
        return closedResponse.data.data.id;
      });

    dispatch(fetchListingSuccess(response, unavailableIds));
    return response;
  } catch (error) {
    dispatch(fetchListingError(storableError(error)));
  }
};

export const loadData = () => async (dispatch, getState, sdk) => {
  try {
    const currentUser = await dispatch(fetchCurrentUser());
    const bookmarks = (currentUser?.id && getUserBagData(currentUser)) || localBagData();
    const saveLaterItems = !!currentUser?.id ? getUserSaveLaterData(currentUser) : [];
    const fetchListingArgs = {
      bookmarks,
      saveLaterItems,
    };
    if (bookmarks.length > 0 || saveLaterItems.length > 0) {
      dispatch(fetchListings(fetchListingArgs));
    }
  } catch (error) {}
};
